import { create } from "zustand";
import { UserAuthService } from "../services/userAuth.service";

export const useUserAuthStore = create((set, get) => {
  // --- Safe parse for persisted user ---
  let storedUser = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw && raw !== "undefined" && raw !== "null") {
      storedUser = JSON.parse(raw);
    }
  } catch (err) {
    console.warn("⚠️ Corrupt user data in localStorage — clearing...");
    localStorage.removeItem("user");
  }

  return {
    currentUser: storedUser,
    users: [],
    loading: false,
    error: null,

    // ---------- Authentication ----------
    login: async (email, password) => {
      set({ loading: true, error: null });
      try {
        const user = await UserAuthService.login(email, password);
        localStorage.setItem("user", JSON.stringify(user));
        set({ currentUser: user, loading: false });
        return true;
      } catch (err) {
        set({
          error: err.response?.data?.error || err.message,
          loading: false,
        });
        return false;
      }
    },

    logout: async () => {
      try {
        await UserAuthService.logout();
      } catch {
        /* ignore logout network errors */
      }
      localStorage.removeItem("user");
      set({ currentUser: null });
    },

    checkSession: async () => {
      try {
        const user = await UserAuthService.getProfile();
        localStorage.setItem("user", JSON.stringify(user));
        set({ currentUser: user });
      } catch {
        localStorage.removeItem("user");
        set({ currentUser: null });
      }
    },

    // ---------- User CRUD (Admin Panel) ----------
    fetchUsers: async () => {
      const { loading } = get();
      if (loading) return; // prevent multiple concurrent calls

      set({ loading: true, error: null });
      try {
        const data = await UserAuthService.getAll();
        set({ users: data, loading: false });
      } catch (err) {
        set({
          error: err.response?.data?.error || err.message,
          loading: false,
        });
      }
    },

    registerUser: async (payload) => {
      try {
        const user = await UserAuthService.register(payload);
        set((s) => ({ users: [...s.users, user] }));
        return user;
      } catch (err) {
        alert(`Error creating user: ${err.response?.data?.error || err.message}`);
      }
    },

    updateUser: async (id, payload) => {
      try {
        const updated = await UserAuthService.update(id, payload);
        set((s) => ({
          users: s.users.map((u) => (u.id === id ? updated : u)),
        }));
        return updated;
      } catch (err) {
        alert(`Error updating user: ${err.response?.data?.error || err.message}`);
      }
    },

    deleteUser: async (id) => {
      if (!window.confirm("Delete this user?")) return;
      try {
        await UserAuthService.delete(id);
        set((s) => ({ users: s.users.filter((u) => u.id !== id) }));
      } catch (err) {
        alert(`Error deleting user: ${err.response?.data?.error || err.message}`);
      }
    },
  };
});
